import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Benchmark,
  RSTimeframe,
  RelativeStrengthRow,
  useRelativeStrength,
} from "@/hooks/useRelativeStrength";

const TIMEFRAMES: { key: RSTimeframe; label: string }[] = [
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
];

const BENCHMARKS: { key: Benchmark; label: string; benchId: string }[] = [
  { key: "btc", label: "vs BTC", benchId: "bitcoin" },
  { key: "eth", label: "vs ETH", benchId: "ethereum" },
];

type SortKey = "coin_return" | "alpha" | "ratio";

function fmtPct(n: number) {
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toFixed(2)}%`;
}

function fmtNum(n: number) {
  return n.toFixed(2);
}

function colorClass(n: number) {
  if (n > 0.01) return "text-success";
  if (n < -0.01) return "text-destructive";
  return "text-muted-foreground";
}

function shorterTf(tf: RSTimeframe): RSTimeframe | null {
  if (tf === "30d") return "7d";
  if (tf === "90d") return "30d";
  return null;
}

export function RelativeStrengthTable() {
  const [benchmark, setBenchmark] = useState<Benchmark>("btc");
  const [timeframe, setTimeframe] = useState<RSTimeframe>("30d");
  const [sortKey, setSortKey] = useState<SortKey>("alpha");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [guideOpen, setGuideOpen] = useState(false);

  const { data, isLoading } = useRelativeStrength();

  const benchmarkMeta = BENCHMARKS.find((b) => b.key === benchmark)!;
  const benchmarkId = benchmarkMeta.benchId;
  const alphaKey = benchmark === "btc" ? "vs_btc_alpha" : "vs_eth_alpha";
  const ratioKey = benchmark === "btc" ? "vs_btc_ratio" : "vs_eth_ratio";
  const benchReturnKey = benchmark === "btc" ? "btc_return" : "eth_return";

  const currentRows = useMemo(
    () => (data ?? []).filter((r) => r.timeframe === timeframe),
    [data, timeframe]
  );

  const shortTf = shorterTf(timeframe);
  const shortRows = useMemo(
    () => (shortTf ? (data ?? []).filter((r) => r.timeframe === shortTf) : []),
    [data, shortTf]
  );

  const sortedRows = useMemo(() => {
    const rows = [...currentRows];
    const dir = sortDir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let va = 0;
      let vb = 0;
      if (sortKey === "coin_return") {
        va = a.coin_return;
        vb = b.coin_return;
      } else if (sortKey === "alpha") {
        va = (a as any)[alphaKey];
        vb = (b as any)[alphaKey];
      } else {
        va = (a as any)[ratioKey];
        vb = (b as any)[ratioKey];
      }
      return (va - vb) * dir;
    });
    return rows;
  }, [currentRows, sortKey, sortDir, alphaKey, ratioKey]);

  const maxAbsAlpha = useMemo(() => {
    let m = 0;
    for (const r of currentRows) {
      const v = Math.abs((r as any)[alphaKey] as number);
      if (v > m) m = v;
    }
    return m || 1;
  }, [currentRows, alphaKey]);

  const nonBenchRows = currentRows.filter((r) => r.coin_id !== benchmarkId);
  const outperformCount = nonBenchRows.filter((r) => ((r as any)[alphaKey] as number) > 0).length;
  const totalNonBench = nonBenchRows.length;

  const strongest = [...nonBenchRows].sort(
    (a, b) => ((b as any)[alphaKey] as number) - ((a as any)[alphaKey] as number)
  )[0];
  const weakest = [...nonBenchRows].sort(
    (a, b) => ((a as any)[alphaKey] as number) - ((b as any)[alphaKey] as number)
  )[0];

  const breadthLabel =
    outperformCount < 4
      ? "limited altcoin strength — capital concentrating in BTC"
      : outperformCount <= 6
      ? "mixed signals — selective altcoin strength"
      : "broad altcoin strength — capital rotating into alts";

  const computedAt = currentRows[0]?.computed_at;
  const staleHours = computedAt
    ? (Date.now() - new Date(computedAt).getTime()) / (1000 * 60 * 60)
    : 0;
  const isStale = staleHours > 48;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  const trendFor = (r: RelativeStrengthRow) => {
    if (!shortTf) return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
    const cur = (r as any)[alphaKey] as number;
    const prev = shortRows.find((s) => s.coin_id === r.coin_id);
    if (!prev) return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
    const prevV = (prev as any)[alphaKey] as number;
    if (cur - prevV > 0.05) return <TrendingUp className="w-3.5 h-3.5 text-success" />;
    if (cur - prevV < -0.05) return <TrendingDown className="w-3.5 h-3.5 text-destructive" />;
    return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
  };

  return (
    <div className="space-y-6">
      {/* Toggles */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-3">
          <div className="inline-flex rounded-full border border-border/60 p-0.5 bg-card/40">
            {BENCHMARKS.map((b) => (
              <button
                key={b.key}
                onClick={() => setBenchmark(b.key)}
                className={cn(
                  "px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded-full transition-colors",
                  benchmark === b.key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {b.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-border/60 p-0.5 bg-card/40">
            {TIMEFRAMES.map((t) => (
              <button
                key={t.key}
                onClick={() => setTimeframe(t.key)}
                className={cn(
                  "px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded-full transition-colors",
                  timeframe === t.key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        {isStale && computedAt && (
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border border-yellow-500/40 bg-yellow-500/10 text-yellow-500">
            Stale · {Math.round(staleHours)}h old
          </span>
        )}
      </div>

      {/* Table */}
      <div className="border border-border/60 rounded-lg overflow-hidden bg-card/20">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-10 rounded bg-muted/30 animate-pulse" />
            ))}
          </div>
        ) : currentRows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Relative strength data is being computed and will be available shortly. Data refreshes daily at
            6:15 AM AEST.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/20 border-b border-border/60">
                <tr className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2.5 text-left w-12">#</th>
                  <th className="px-3 py-2.5 text-left">Coin</th>
                  <th className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => handleSort("coin_return")}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      Return <SortIcon k="coin_return" />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 text-right hidden md:table-cell">
                    {benchmark.toUpperCase()} Return
                  </th>
                  <th className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => handleSort("alpha")}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      Alpha <SortIcon k="alpha" />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 w-40 hidden lg:table-cell">Distribution</th>
                  <th className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => handleSort("ratio")}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      RS Ratio <SortIcon k="ratio" />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 text-center w-16">Trend</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, idx) => {
                  const isBench = r.coin_id === benchmarkId;
                  const alpha = (r as any)[alphaKey] as number;
                  const ratio = (r as any)[ratioKey] as number;
                  const benchRet = (r as any)[benchReturnKey] as number;
                  const barPct = Math.min(100, (Math.abs(alpha) / maxAbsAlpha) * 100);
                  return (
                    <tr
                      key={r.coin_id}
                      className={cn(
                        "border-b border-border/40 last:border-0 hover:bg-muted/20 transition-colors",
                        idx % 2 === 1 && "bg-muted/5",
                        isBench && "opacity-70"
                      )}
                    >
                      <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{idx + 1}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold">{r.coin_symbol}</span>
                          <span className="text-muted-foreground text-xs hidden sm:inline">
                            {r.coin_name}
                          </span>
                          {isBench && (
                            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-border/60 text-muted-foreground">
                              Benchmark
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={cn("px-3 py-2.5 text-right font-mono", colorClass(r.coin_return))}>
                        {fmtPct(r.coin_return)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-muted-foreground hidden md:table-cell">
                        {fmtPct(benchRet)}
                      </td>
                      <td className={cn("px-3 py-2.5 text-right font-mono font-bold", colorClass(alpha))}>
                        {fmtPct(alpha)}
                      </td>
                      <td className="px-3 py-2.5 hidden lg:table-cell">
                        <div className="relative h-2 w-full bg-muted/20 rounded-full overflow-hidden">
                          <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
                          {alpha >= 0 ? (
                            <div
                              className="absolute inset-y-0 left-1/2 bg-success/70 rounded-r-full"
                              style={{ width: `${barPct / 2}%` }}
                            />
                          ) : (
                            <div
                              className="absolute inset-y-0 right-1/2 bg-destructive/70 rounded-l-full"
                              style={{ width: `${barPct / 2}%` }}
                            />
                          )}
                        </div>
                      </td>
                      <td className={cn("px-3 py-2.5 text-right font-mono", colorClass(ratio - 100))}>
                        {fmtNum(ratio)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="inline-flex">{trendFor(r)}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Insights */}
      {currentRows.length > 0 && (
        <div className="grid gap-3 md:grid-cols-3">
          {strongest && (
            <div className="border border-border/60 rounded-lg p-4 bg-card/20">
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
                Strongest Outperformer
              </div>
              <div className="text-sm">
                <span className="font-semibold">{strongest.coin_symbol}</span>{" "}
                <span className={cn("font-mono", colorClass((strongest as any)[alphaKey]))}>
                  ({fmtPct((strongest as any)[alphaKey])} vs {benchmark.toUpperCase()})
                </span>
                <div className="text-xs text-muted-foreground mt-1">
                  strongest independent momentum over {timeframe}
                </div>
              </div>
            </div>
          )}
          {weakest && (
            <div className="border border-border/60 rounded-lg p-4 bg-card/20">
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
                Weakest Performer
              </div>
              <div className="text-sm">
                <span className="font-semibold">{weakest.coin_symbol}</span>{" "}
                <span className={cn("font-mono", colorClass((weakest as any)[alphaKey]))}>
                  ({fmtPct((weakest as any)[alphaKey])} vs {benchmark.toUpperCase()})
                </span>
                <div className="text-xs text-muted-foreground mt-1">
                  significant underperformance against {benchmark.toUpperCase()}
                </div>
              </div>
            </div>
          )}
          <div className="border border-border/60 rounded-lg p-4 bg-card/20">
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
              Breadth
            </div>
            <div className="text-sm">
              <span className="font-mono font-semibold">
                {outperformCount} of {totalNonBench}
              </span>{" "}
              coins outperforming {benchmark.toUpperCase()} over {timeframe}
              <div className="text-xs text-muted-foreground mt-1">{breadthLabel}</div>
            </div>
          </div>
        </div>
      )}

      {/* Freshness */}
      {computedAt && (
        <div className="text-[11px] font-mono text-muted-foreground">
          Last computed:{" "}
          {new Intl.DateTimeFormat("en-AU", {
            timeZone: "Australia/Sydney",
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date(computedAt))}{" "}
          AEST
        </div>
      )}

      {/* Guide */}
      <div className="border border-border/60 rounded-lg bg-card/20">
        <button
          onClick={() => setGuideOpen(!guideOpen)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
            How to read this
          </span>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-muted-foreground transition-transform",
              guideOpen && "rotate-180"
            )}
          />
        </button>
        {guideOpen && (
          <div className="px-4 pb-4 space-y-2 text-xs text-muted-foreground leading-relaxed">
            <p>
              <span className="text-foreground font-semibold">Alpha &gt; 0:</span> This coin outperformed
              the benchmark over the period. A +5% alpha means it returned 5 percentage points more than
              BTC (or ETH).
            </p>
            <p>
              <span className="text-foreground font-semibold">Alpha &lt; 0:</span> This coin
              underperformed the benchmark. Even if it went up in AUD terms, it lost ground relative to
              simply holding BTC.
            </p>
            <p>
              <span className="text-foreground font-semibold">RS Ratio &gt; 100:</span> Outperforming.
              The further above 100, the stronger the outperformance.
            </p>
            <p>
              <span className="text-foreground font-semibold">RS Ratio &lt; 100:</span>{" "}
              Underperforming. The further below 100, the worse the relative performance.
            </p>
            <p>
              <span className="text-foreground font-semibold">Why this matters:</span> In crypto, most
              altcoins are highly correlated with BTC. A coin with strong positive alpha is showing{" "}
              <em>independent</em> strength — a much higher-quality signal than simply going up because
              the whole market went up.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
